import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';
import styles from './styles.module.scss';

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';
import WarehouseService from '../../../../../services/warehouses';

// props
import { IWarehouseResourceShortProps } from '../../../../../props/warehouses/warehouse';

// components
import { ActionButton, CommandBar, IColumn, ICommandBarItemProps, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import Text from '../../../../typography/text';
import WarehouseForm from '../form';

interface IWarehousesListProps {
}

const WarehousesList: React.FC<IWarehousesListProps> = (props: IWarehousesListProps) => {
    const { banner } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [warehouses, setWarehouses] = React.useState<IWarehouseResourceShortProps[]>([]);
    const [activeWarehouse, setActiveWarehouse] = React.useState<IWarehouseResourceShortProps | undefined>();
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const columns: IColumn[] = [
        {
            key: "name",
            fieldName: "name",
            name: "Name",
            minWidth: 300,
            maxWidth: 300,
            onRender: (item: IWarehouseResourceShortProps) => {
                return <Stack styles={{root: {height: '100%'}}}>
                    <Text>{item.name}</Text>
                </Stack>;
            }
        },
        {
            key: "actions",
            name: "",
            minWidth: 50,
            maxWidth: 50,
            onRender: (item: IWarehouseResourceShortProps) => {
                return <Stack styles={{root: {height: '100%'}}}>
                    <Stack.Item className={"detailsListActionRow"}>
                        <ActionButton className={'detailsListActionButton'} menuProps={{
                            items: [
                                {key: "update", text: "Update", iconProps: {iconName: "Edit"}, onClick: () => {
                                    setActiveWarehouse(item);
                                    setActiveSurface('update');
                                }}
                            ]
                        }} />
                    </Stack.Item>
                </Stack>;
            }
        },
        {
            key: "address",
            fieldName: "address",
            name: "Address",
            minWidth: 0,
            isMultiline: true,
            onRender: (item: IWarehouseResourceShortProps) => {
                return <Stack styles={{root: {height: '100%'}}}>
                    <Text>{item.address}</Text>
                </Stack>;
            }
        }
    ];

    useEffect(() => {
        _onRetrieveWarehouses();
    }, []);

    const _onRetrieveWarehouses = async (pageNumber?: number, ) => {
        try {
            if (!pageNumber) {
                setLoaded(false);
            }

            const qs: string[] = [];
            const results = await WarehouseService.retrieve.get(qs.join(""));
            setWarehouses(results);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_warehouses_error',
                text: 'Failed to retrieve warehouses. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onKeywordChanged = (value?:string) => {
        setLoaded(true);
        setKeyword(value || "");
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        items.push({
            key: "search",
            onRender: () => {
                return <SearchBox placeholder={"Search ..."} onSearch={_onKeywordChanged} />
            }
        });

        farItems.push({
            key: "register",
            text: "Create Warehouse",
            iconProps: { iconName: "Add" },
            onRender: () => { 
                return <PrimaryButton text={"Create Warehouse"} 
                    iconProps={{ iconName: "Add" }} 
                    onClick={() => {setActiveSurface('create') }} 
                    styles={{ root: { marginLeft: 10 } }} />;
            }
        });

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        setActiveWarehouse(undefined);

        if (refresh) {_onRetrieveWarehouses()}
    }

    return <Stack className={styles.container} tokens={{childrenGap: 20}}>
        <CommandBar
            items={getCommandBarItems().items}
            farItems={getCommandBarItems().farItems}
            styles={{root: {
                padding: 0,
                height: 'unset',
                backgroundColor: 'transparent'
            }}}
            ariaLabel="Use left and right arrow keys to navigate between commands" />
        <Stack className={styles.list}>
            {
                !loaded || (loaded && warehouses.length > 0) ? (
                    <>
                        <ShimmeredDetailsList
                            setKey="items"
                            items={warehouses}
                            columns={columns}
                            selectionMode={SelectionMode.none}
                            enableShimmer={!loaded}
                            onShouldVirtualize={() => false}
                            ariaLabelForShimmer="Content is being fetched"
                            ariaLabelForGrid="Item details" />
                    </>
                ) : null
            }
            {loaded && warehouses.length < 1 ? <Stack styles={{root: {padding: 10}}}>
                <Text>Warehouse(s) not found</Text>
            </Stack> : null}
        </Stack>
        {activeSurface === 'create' ? <WarehouseForm onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'update' && activeWarehouse ? <WarehouseForm warehouse={activeWarehouse} onDismissed={_onSurfaceDismissed} /> : null}
    </Stack>;
};

export default WarehousesList;
